import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar, type DateRange } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface DateRangePreset {
  key: string;
  label: string;
  /** Returns the concrete {start, end} for this preset, evaluated when selected. */
  getRange: () => { start: Date; end: Date };
}

export interface DateRangeValue {
  /** A preset key, or 'custom' when an arbitrary range was picked. */
  preset: string;
  start: Date;
  end: Date;
}

interface DateRangeSelectProps {
  presets: DateRangePreset[];
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  className?: string;
  align?: 'start' | 'center' | 'end';
  numberOfMonths?: number;
}

function triggerLabel(value: DateRangeValue, presets: DateRangePreset[]): string {
  if (value.preset !== 'custom') {
    const preset = presets.find((p) => p.key === value.preset);
    if (preset) return preset.label;
  }
  const start = format(value.start, 'MMM d, yyyy');
  const end = format(value.end, 'MMM d, yyyy');
  return start === end ? start : `${start} – ${end}`;
}

/**
 * A date-range control combining quick presets with an arbitrary custom range.
 * Emits the selected preset key (or 'custom') plus the concrete start/end dates,
 * so callers can drive queries directly off value.start / value.end.
 */
export function DateRangeSelect({
  presets,
  value,
  onChange,
  className,
  align = 'start',
  numberOfMonths = 2,
}: DateRangeSelectProps) {
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>({
    from: value.start,
    to: value.end,
  });

  const applyPreset = (preset: DateRangePreset) => {
    const range = preset.getRange();
    onChange({ preset: preset.key, start: range.start, end: range.end });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draftRange?.from) return;
    const start = draftRange.from;
    const end = draftRange.to ?? draftRange.from;
    onChange({ preset: 'custom', start, end });
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDraftRange({ from: value.start, to: value.end });
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('gap-2 justify-start font-normal', className)}>
          <CalendarIcon className="h-4 w-4 shrink-0" />
          <span className="truncate">{triggerLabel(value, presets)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-3 space-y-3">
        <div className="flex flex-wrap gap-2 max-w-[20rem]">
          {presets.map((preset) => (
            <Button
              key={preset.key}
              size="sm"
              variant={value.preset === preset.key ? 'default' : 'outline'}
              onClick={() => applyPreset(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
        <div className="border-t pt-3 space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Custom range</p>
          <Calendar
            mode="range"
            defaultMonth={value.start}
            selected={draftRange}
            onSelect={setDraftRange}
            numberOfMonths={numberOfMonths}
            className="rounded-lg border shadow-sm"
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={applyCustom} disabled={!draftRange?.from}>
              Apply range
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
